import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Form, Modal, Nav, Spinner, Table, Tab } from "react-bootstrap";
import {
  FaArrowLeft, FaEdit, FaExclamationTriangle, FaGripVertical, FaPlus, FaTimes, FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api";
import Can from "../../components/Can";
import EmployeePicker from "../../components/shared/EmployeePicker";
import "./KraKpiConfigurationDetailPage.css";

// Task spec §2/§5/§6/§8/§10/§11/§12/§13/§15: one page, five tabs, covering
// the full KRA -> KPI -> (rating rules / evidence mapping) hierarchy plus
// employee assignments. Every mutation re-fetches the whole configuration
// tree from the server afterward -- never a frontend-only state update
// (§17: "Do not implement frontend-only state updates").
const MODULE = "kra_kpi_management";
const CALCULATION_TYPES = ['RATIO_HIGHER_BETTER', 'RATIO_LOWER_BETTER', 'AVERAGE_SCORE', 'COUNT_CUMULATIVE', 'RATING_SCALE', 'FORM_SCORE', 'MANUAL'];
const EVIDENCE_SOURCES = ['TASK_TYPE', 'FORM', 'MANUAL', 'MIXED'];

function errMsg(err, fallback) {
  return err?.response?.data?.message || fallback;
}

export default function KraKpiConfigurationDetailPage() {
  const { configurationId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [weightage, setWeightage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("configuration");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, weightageRes] = await Promise.all([
        api.get(`/admin/kra-kpi/configurations/${configurationId}`),
        api.get(`/admin/kra-kpi/configurations/${configurationId}/weightage-check`).catch(() => null),
      ]);
      setConfig(configRes.data?.data || null);
      setWeightage(weightageRes?.data?.data || null);
    } catch (err) {
      toast.error(errMsg(err, "Failed to load configuration."));
    } finally {
      setLoading(false);
    }
  }, [configurationId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading && !config) {
    return <div className="unit-page text-center py-5"><Spinner animation="border" /></div>;
  }
  if (!config) {
    return <div className="unit-page text-center py-5 text-muted">Configuration not found.</div>;
  }

  return (
    <div className="unit-page kkd-page">
      <div className="kkd-header">
        <Button
          variant="link"
          className="kkd-back-btn"
          onClick={() => navigate("/admin/kra-kpi-configurations")}
          aria-label="Back to KRA / KPI Configurations"
          title="Back to KRA / KPI Configurations"
        >
          <FaArrowLeft aria-hidden="true" />
        </Button>
        <div className="kkd-header-text">
          <h2 className="kkd-title">{config.name}</h2>
          <div className="kkd-meta">
            <span className="kkd-code">{config.code}</span>
            <Badge className={`unit-status${config.is_active ? "" : " unit-status-inactive"}`}>
              {config.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
        </div>
      </div>

      {weightage && !weightage.valid ? (
        <div className="alert alert-warning d-flex align-items-start gap-2 kkd-alert">
          <FaExclamationTriangle className="mt-1" aria-hidden="true" />
          <div>
            <strong>Weightage validation failed — this configuration cannot be activated until resolved:</strong>
            <ul className="mb-0 mt-1">
              {weightage.issues.map((issue, i) => <li key={i}>{issue.message}</li>)}
            </ul>
          </div>
        </div>
      ) : null}

      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Nav variant="tabs" className="kkd-tabs">
          <Nav.Item><Nav.Link eventKey="configuration">Configuration</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="kras-kpis">KRAs & KPIs</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="rating-rules">Rating Rules</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="evidence-mapping">Evidence Mapping</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="assignments">Employee Assignments</Nav.Link></Nav.Item>
        </Nav>
        <Tab.Content className="kkd-tab-content">
          <Tab.Pane eventKey="configuration"><ConfigurationTab config={config} onSaved={fetchAll} /></Tab.Pane>
          <Tab.Pane eventKey="kras-kpis"><KraKpiTab config={config} onChanged={fetchAll} /></Tab.Pane>
          <Tab.Pane eventKey="rating-rules"><RatingRulesTab config={config} onChanged={fetchAll} /></Tab.Pane>
          <Tab.Pane eventKey="evidence-mapping"><EvidenceMappingTab config={config} onChanged={fetchAll} /></Tab.Pane>
          <Tab.Pane eventKey="assignments"><AssignmentsTab configurationId={config.id} /></Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
}

// =============================================================================
// Tab: Configuration
// =============================================================================
function ConfigurationTab({ config, onSaved }) {
  const [form, setForm] = useState({ name: config.name, description: config.description || "", performance_year_start_month: config.performance_year_start_month });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/kra-kpi/configurations/${config.id}`, form);
      toast.success("Configuration saved.");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, "Failed to save."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Can moduleKey={MODULE} actionKey="edit" fallback={<ReadOnlyNotice />}>
      <div className="unit-card kkd-config-card">
        <div className="kkd-card-body">
          <Form.Group className="kkd-field">
            <Form.Label>Configuration Name</Form.Label>
            <Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Form.Group>
          <Form.Group className="kkd-field">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              className="kkd-textarea"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="kkd-field kkd-field-month">
            <Form.Label>Performance Year Start Month</Form.Label>
            <Form.Select value={form.performance_year_start_month} onChange={(e) => setForm({ ...form, performance_year_start_month: Number(e.target.value) })}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
            </Form.Select>
          </Form.Group>
        </div>
        <div className="kkd-form-footer">
          <Button className="unit-btn unit-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" /> : "Save Changes"}
          </Button>
        </div>
      </div>
    </Can>
  );
}

function ReadOnlyNotice() {
  return <div className="text-muted">You have view-only access to this configuration.</div>;
}

function monthName(m) {
  const names = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return names[m] || "—";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

// =============================================================================
// Tab: KRAs & KPIs
// =============================================================================
function KraKpiTab({ config, onChanged }) {
  const [kraModal, setKraModal] = useState(null); // { kra } | { configurationId } | null
  const [kpiModal, setKpiModal] = useState(null); // { kpi, kraId } | { kraId } | null
  const [confirmDelete, setConfirmDelete] = useState(null); // { type: 'kra'|'kpi', row }
  const [dragIndex, setDragIndex] = useState(null);

  const kraTotal = round2(config.kras.filter((k) => k.is_active).reduce((s, k) => s + Number(k.weightage_percent), 0));

  const handleReorderKras = async (fromIndex, toIndex) => {
    const ids = [...config.kras].map((k) => k.id);
    const [moved] = ids.splice(fromIndex, 1);
    ids.splice(toIndex, 0, moved);
    try {
      await api.put(`/admin/kra-kpi/configurations/${config.id}/kras/reorder`, { ordered_ids: ids });
      onChanged();
    } catch (err) { toast.error(errMsg(err, "Failed to reorder.")); }
  };

  const handleKraStatus = async (kra) => {
    try {
      await api.patch(`/admin/kra-kpi/kras/${kra.id}/status`, { is_active: !kra.is_active });
      onChanged();
    } catch (err) { toast.error(errMsg(err, "Failed to update status.")); }
  };

  const handleKpiStatus = async (kpi) => {
    try {
      await api.patch(`/admin/kra-kpi/kpis/${kpi.id}/status`, { is_active: !kpi.is_active });
      onChanged();
    } catch (err) { toast.error(errMsg(err, "Failed to update status.")); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.type === "kra") await api.delete(`/admin/kra-kpi/kras/${confirmDelete.row.id}`);
      else await api.delete(`/admin/kra-kpi/kpis/${confirmDelete.row.id}`);
      toast.success("Deleted.");
      setConfirmDelete(null);
      onChanged();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === "KRA_IN_USE" || data?.code === "KPI_IN_USE") {
        toast.error(`${data.message} (Deactivate it instead.)`);
      } else {
        toast.error(errMsg(err, "Failed to delete."));
      }
      setConfirmDelete(null);
    }
  };

  return (
    <div className="kkd-kra-kpi-tab">
      <div className="kkd-summary-toolbar">
        <div className={`kkd-weight-summary${kraTotal === 100 ? " kkd-weight-summary--ok" : " kkd-weight-summary--warn"}`}>
          <span className="kkd-weight-label">KRA Weightage</span>
          <span className="kkd-weight-value">{kraTotal} / 100</span>
        </div>
        <Can moduleKey={MODULE} actionKey="edit">
          <Button className="unit-btn unit-btn-primary" onClick={() => setKraModal({ configurationId: config.id })}>
            <FaPlus aria-hidden="true" /> Add KRA
          </Button>
        </Can>
      </div>

      {config.kras.map((kra, index) => {
        const kpiTotal = round2(kra.kpis.filter((k) => k.is_active).reduce((s, k) => s + Number(k.weightage_percent), 0));
        return (
          <div
            key={kra.id}
            className="unit-card kkd-kra-card"
            draggable
            onDragStart={() => setDragIndex(index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragIndex !== null && dragIndex !== index) handleReorderKras(dragIndex, index); setDragIndex(null); }}
          >
            <div className="kkd-kra-header">
              <div className="kkd-kra-header-main">
                <span className="kkd-drag-handle" title="Drag to reorder" aria-label={`Drag to reorder ${kra.name}`}>
                  <FaGripVertical aria-hidden="true" />
                </span>
                <div className="kkd-kra-title-block">
                  <span className="kkd-kra-name">{kra.name}</span>
                  {!kra.is_active && <Badge className="unit-status unit-status-inactive kkd-inline-status">Inactive</Badge>}
                </div>
              </div>
              <div className="kkd-kra-header-side">
                <span className="kkd-weight-badge">{kra.weightage_percent}%</span>
                <Can moduleKey={MODULE} actionKey="edit">
                  <div className="unit-actions kkd-kra-actions">
                    <Button className="unit-icon-btn kkd-action-edit" title="Edit KRA" aria-label={`Edit ${kra.name}`} onClick={() => setKraModal({ kra })}>
                      <FaEdit aria-hidden="true" />
                    </Button>
                    <Can moduleKey={MODULE} actionKey="activate">
                      <Form.Check
                        type="switch"
                        className="kkd-switch"
                        checked={!!kra.is_active}
                        onChange={() => handleKraStatus(kra)}
                        title={kra.is_active ? "Deactivate KRA" : "Activate KRA"}
                        aria-label={`${kra.is_active ? "Deactivate" : "Activate"} ${kra.name}`}
                      />
                    </Can>
                    <Can moduleKey={MODULE} actionKey="delete">
                      <Button className="unit-icon-btn unit-delete kkd-action-delete" title="Delete KRA" aria-label={`Delete ${kra.name}`} onClick={() => setConfirmDelete({ type: "kra", row: kra })}>
                        <FaTrash aria-hidden="true" />
                      </Button>
                    </Can>
                  </div>
                </Can>
              </div>
            </div>

            <div className={`kkd-kpi-weight-helper${kpiTotal === Number(kra.weightage_percent) ? " kkd-kpi-weight-helper--ok" : " kkd-kpi-weight-helper--warn"}`}>
              KPI weighting inside this KRA: {kpiTotal} / {kra.weightage_percent}
            </div>

            <div className="kkd-kpi-table-wrapper">
              <Table className="unit-table kkd-kpi-table">
                <colgroup>
                  <col style={{ width: "31%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>KPI</th>
                    <th className="text-center">Weightage</th>
                    <th>Calc. Type</th>
                    <th>Evidence Source</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {kra.kpis.map((kpi) => (
                    <tr key={kpi.id}>
                      <td className="kkd-kpi-name">{kpi.name}</td>
                      <td className="text-center">{kpi.weightage_percent}%</td>
                      <td><span className="kkd-code-value">{kpi.calculation_type}</span></td>
                      <td>
                        {kpi.evidence_source}
                        {kpi.is_approximation ? <span className="kkd-approx-badge">approx.</span> : null}
                      </td>
                      <td>
                        <Badge className={`unit-status${kpi.is_active ? "" : " unit-status-inactive"}`}>
                          {kpi.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td>
                        <Can moduleKey={MODULE} actionKey="edit">
                          <div className="unit-actions kkd-kpi-actions justify-content-end">
                            <Button className="unit-icon-btn kkd-action-edit" title="Edit KPI" aria-label={`Edit ${kpi.name}`} onClick={() => setKpiModal({ kpi, kraId: kra.id })}>
                              <FaEdit aria-hidden="true" />
                            </Button>
                            <Can moduleKey={MODULE} actionKey="activate">
                              <Form.Check
                                type="switch"
                                className="kkd-switch"
                                checked={!!kpi.is_active}
                                onChange={() => handleKpiStatus(kpi)}
                                title={kpi.is_active ? "Deactivate KPI" : "Activate KPI"}
                                aria-label={`${kpi.is_active ? "Deactivate" : "Activate"} ${kpi.name}`}
                              />
                            </Can>
                            <Can moduleKey={MODULE} actionKey="delete">
                              <Button className="unit-icon-btn unit-delete kkd-action-delete" title="Delete KPI" aria-label={`Delete ${kpi.name}`} onClick={() => setConfirmDelete({ type: "kpi", row: kpi })}>
                                <FaTrash aria-hidden="true" />
                              </Button>
                            </Can>
                          </div>
                        </Can>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <Can moduleKey={MODULE} actionKey="edit">
              <Button className="kkd-add-kpi-btn" onClick={() => setKpiModal({ kraId: kra.id })}>
                <FaPlus aria-hidden="true" /> Add KPI
              </Button>
            </Can>
          </div>
        );
      })}

      {kraModal ? <KraModal payload={kraModal} onClose={() => setKraModal(null)} onSaved={() => { setKraModal(null); onChanged(); }} /> : null}
      {kpiModal ? <KpiModal payload={kpiModal} onClose={() => setKpiModal(null)} onSaved={() => { setKpiModal(null); onChanged(); }} /> : null}

      <Modal show={!!confirmDelete} onHide={() => setConfirmDelete(null)} centered>
        <Modal.Header closeButton><Modal.Title>Delete {confirmDelete?.type === "kra" ? "KRA" : "KPI"}</Modal.Title></Modal.Header>
        <Modal.Body>
          Delete <strong>{confirmDelete?.row?.name}</strong>? If it already has historical evidence/results this will be blocked automatically — deactivate it instead.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function round2(v) { return Math.round(Number(v) * 100) / 100; }

function KraModal({ payload, onClose, onSaved }) {
  const editing = payload.kra || null;
  const [form, setForm] = useState({ name: editing?.name || "", code: editing?.code || "", weightage_percent: editing?.weightage_percent ?? 0 });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/kra-kpi/kras/${editing.id}`, form);
      else await api.post(`/admin/kra-kpi/configurations/${payload.configurationId}/kras`, form);
      toast.success("KRA saved.");
      onSaved();
    } catch (err) { toast.error(errMsg(err, "Failed to save KRA.")); } finally { setSaving(false); }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>{editing ? "Edit KRA" : "Add KRA"}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3"><Form.Label>Name</Form.Label><Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Form.Group>
        <Form.Group className="mb-3"><Form.Label>Code</Form.Label><Form.Control value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!editing} placeholder="Auto-generated from name if left blank" /></Form.Group>
        <Form.Group className="mb-3"><Form.Label>Weightage %</Form.Label><Form.Control type="number" min={0} max={100} value={form.weightage_percent} onChange={(e) => setForm({ ...form, weightage_percent: Number(e.target.value) })} /></Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !form.name.trim()}>{saving ? <Spinner size="sm" animation="border" /> : "Save"}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function KpiModal({ payload, onClose, onSaved }) {
  const editing = payload.kpi || null;
  const [form, setForm] = useState({
    name: editing?.name || "", code: editing?.code || "", description: editing?.description || "",
    calculation_type: editing?.calculation_type || "MANUAL", weightage_percent: editing?.weightage_percent ?? 0,
    annual_target_value: editing?.annual_target_value ?? "", annual_target_label: editing?.annual_target_label || "",
    unit: editing?.unit || "", evidence_source: editing?.evidence_source || "MANUAL", is_approximation: !!editing?.is_approximation,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payloadBody = { ...form, annual_target_value: form.annual_target_value === "" ? null : Number(form.annual_target_value) };
      if (editing) await api.put(`/admin/kra-kpi/kpis/${editing.id}`, payloadBody);
      else await api.post(`/admin/kra-kpi/kras/${payload.kraId}/kpis`, payloadBody);
      toast.success("KPI saved.");
      onSaved();
    } catch (err) { toast.error(errMsg(err, "Failed to save KPI.")); } finally { setSaving(false); }
  };

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton><Modal.Title>{editing ? "Edit KPI" : "Add KPI"}</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-6"><Form.Group><Form.Label>Name</Form.Label><Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Form.Group></div>
          <div className="col-md-6"><Form.Group><Form.Label>Code</Form.Label><Form.Control value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!editing} placeholder="Auto-generated if blank" /></Form.Group></div>
          <div className="col-12"><Form.Group><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Form.Group></div>
          <div className="col-md-4">
            <Form.Group><Form.Label>Calculation Type</Form.Label>
              <Form.Select value={form.calculation_type} onChange={(e) => setForm({ ...form, calculation_type: e.target.value })}>
                {CALCULATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-4"><Form.Group><Form.Label>Weightage %</Form.Label><Form.Control type="number" min={0} max={100} value={form.weightage_percent} onChange={(e) => setForm({ ...form, weightage_percent: Number(e.target.value) })} /></Form.Group></div>
          <div className="col-md-4">
            <Form.Group><Form.Label>Evidence Source</Form.Label>
              <Form.Select value={form.evidence_source} onChange={(e) => setForm({ ...form, evidence_source: e.target.value })}>
                {EVIDENCE_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-4"><Form.Group><Form.Label>Target Value</Form.Label><Form.Control type="number" value={form.annual_target_value} onChange={(e) => setForm({ ...form, annual_target_value: e.target.value })} /></Form.Group></div>
          <div className="col-md-4"><Form.Group><Form.Label>Target Label</Form.Label><Form.Control value={form.annual_target_label} onChange={(e) => setForm({ ...form, annual_target_label: e.target.value })} placeholder="e.g. >=90% delivered as planned" /></Form.Group></div>
          <div className="col-md-4"><Form.Group><Form.Label>Unit</Form.Label><Form.Control value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="PERCENT, COUNT, SCORE_1_5..." /></Form.Group></div>
          <div className="col-12">
            <Form.Check type="checkbox" label="This KPI is an approximation (best-effort proxy, not the literal business metric)" checked={form.is_approximation} onChange={(e) => setForm({ ...form, is_approximation: e.target.checked })} />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !form.name.trim()}>{saving ? <Spinner size="sm" animation="border" /> : "Save"}</Button>
      </Modal.Footer>
    </Modal>
  );
}

// =============================================================================
// Tab: Rating Rules
// =============================================================================
function RatingRulesTab({ config, onChanged }) {
  const allKpis = useMemo(() => config.kras.flatMap((kra) => kra.kpis.map((kpi) => ({ ...kpi, kra_name: kra.name }))), [config]);
  const [selectedKpiId, setSelectedKpiId] = useState(allKpis[0]?.id || null);
  const selected = allKpis.find((k) => k.id === selectedKpiId);

  return (
    <div className="kkd-rating-rules-tab">
      <div className="unit-card kkd-thresholds-card">
        <div className="kkd-card-body">
          <Form.Group className="kkd-field kkd-kpi-select-field">
            <Form.Label>KPI</Form.Label>
            <Form.Select
              value={selectedKpiId || ""}
              onChange={(e) => setSelectedKpiId(Number(e.target.value))}
              title={selected ? `${selected.kra_name} — ${selected.name}` : undefined}
            >
              {allKpis.map((k) => <option key={k.id} value={k.id}>{k.kra_name} — {k.name}</option>)}
            </Form.Select>
          </Form.Group>
          {selected ? <ThresholdsSection key={selected.id} kpi={selected} onChanged={onChanged} /> : <div className="text-muted kkd-empty-note">No KPIs configured yet.</div>}
        </div>
      </div>
      {selected ? <SecondaryTargetSection key={selected.id} kpi={selected} onChanged={onChanged} /> : null}
    </div>
  );
}

function ThresholdsSection({ kpi, onChanged }) {
  const [thresholds, setThresholds] = useState(() => {
    const byRating = new Map(kpi.rating_thresholds.map((t) => [t.rating, t]));
    return [5, 4, 3, 2, 1].map((r) => byRating.get(r) || { rating: r, min_value: "", max_value: "" });
  });
  const [saving, setSaving] = useState(false);

  const updateThreshold = (rating, field, value) => {
    setThresholds((prev) => prev.map((t) => (t.rating === rating ? { ...t, [field]: value } : t)));
  };

  const saveThresholds = async () => {
    setSaving(true);
    try {
      const payload = thresholds.map((t) => ({
        rating: t.rating,
        min_value: t.min_value === "" || t.min_value === null ? null : Number(t.min_value),
        max_value: t.max_value === "" || t.max_value === null ? null : Number(t.max_value),
      }));
      await api.put(`/admin/kra-kpi/kpis/${kpi.id}/rating-thresholds`, { thresholds: payload });
      toast.success("Rating thresholds saved.");
      onChanged();
    } catch (err) { toast.error(errMsg(err, "Failed to save thresholds.")); } finally { setSaving(false); }
  };

  const isLowerBetter = kpi.calculation_type === "RATIO_LOWER_BETTER";

  return (
    <div className="kkd-thresholds-section">
      <div className="kkd-section-heading-row">
        <h3 className="kkd-section-heading">Rating Thresholds</h3>
        {isLowerBetter ? <span className="kkd-info-badge">Lower is better</span> : null}
      </div>
      <div className="kkd-thresholds-table-wrapper">
        <Table className="unit-table kkd-thresholds-table">
          <colgroup>
            <col style={{ width: "120px" }} />
            <col style={{ width: "200px" }} />
            <col style={{ width: "200px" }} />
          </colgroup>
          <thead><tr><th>Rating</th><th>Minimum</th><th>Maximum</th></tr></thead>
          <tbody>
            {thresholds.map((t) => (
              <tr key={t.rating}>
                <td className="kkd-rating-cell">{t.rating} / 5</td>
                <td>
                  <Form.Control className="kkd-threshold-input" type="number" value={t.min_value ?? ""} onChange={(e) => updateThreshold(t.rating, "min_value", e.target.value)} placeholder="—" />
                </td>
                <td>
                  <Form.Control className="kkd-threshold-input" type="number" value={t.max_value ?? ""} onChange={(e) => updateThreshold(t.rating, "max_value", e.target.value)} placeholder="—" />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Can moduleKey={MODULE} actionKey="manage_rating_rules">
        <div className="kkd-form-footer kkd-form-footer-inline">
          <Button className="unit-btn unit-btn-primary" onClick={saveThresholds} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" /> : "Save Thresholds"}
          </Button>
        </div>
      </Can>
    </div>
  );
}

function SecondaryTargetSection({ kpi, onChanged }) {
  const [secondary, setSecondary] = useState(kpi.secondary_target || null);
  const [showSecondaryForm, setShowSecondaryForm] = useState(false);

  return (
    <div className="unit-card kkd-secondary-card">
      <div className="kkd-card-body">
        <div className="kkd-section-heading-row">
          <h3 className="kkd-section-heading">Secondary Target</h3>
          {!secondary ? <span className="kkd-neutral-badge">Not configured</span> : null}
        </div>
        {secondary && !showSecondaryForm ? (
          <div className="kkd-secondary-summary">
            <div className="kkd-secondary-desc">{secondary.description}</div>
            <div className="kkd-secondary-meta">
              Gate: rating {secondary.gate_min_primary_rating} · Cap if missed: rating {secondary.cap_rating_when_missed} · Target: {secondary.annual_target_value}
            </div>
            <Can moduleKey={MODULE} actionKey="manage_rating_rules">
              <div className="kkd-secondary-actions">
                <Button className="unit-btn unit-btn-light" onClick={() => setShowSecondaryForm(true)}>Edit</Button>
                <Button
                  className="kkd-remove-link-btn"
                  onClick={async () => {
                    await api.delete(`/admin/kra-kpi/kpis/${kpi.id}/secondary-target`);
                    toast.success("Removed.");
                    onChanged();
                  }}
                >
                  Remove
                </Button>
              </div>
            </Can>
          </div>
        ) : (
          <Can moduleKey={MODULE} actionKey="manage_rating_rules" fallback={<div className="text-muted">No secondary target configured.</div>}>
            {showSecondaryForm || !secondary ? (
              <SecondaryTargetForm
                kpiId={kpi.id}
                initial={secondary}
                onSaved={() => { setShowSecondaryForm(false); onChanged(); }}
                onCancel={() => setShowSecondaryForm(false)}
              />
            ) : null}
          </Can>
        )}
      </div>
    </div>
  );
}

function SecondaryTargetForm({ kpiId, initial, onSaved, onCancel }) {
  const [form, setForm] = useState({
    description: initial?.description || "", calculation_type: initial?.calculation_type || "MANUAL",
    annual_target_value: initial?.annual_target_value ?? "", gate_min_primary_rating: initial?.gate_min_primary_rating ?? 5,
    cap_rating_when_missed: initial?.cap_rating_when_missed ?? 4,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/kra-kpi/kpis/${kpiId}/secondary-target`, { ...form, annual_target_value: Number(form.annual_target_value) });
      toast.success("Secondary target saved.");
      onSaved();
    } catch (err) { toast.error(errMsg(err, "Failed to save.")); } finally { setSaving(false); }
  };

  return (
    <div className="kkd-secondary-form">
      <Form.Group className="kkd-field"><Form.Label>Description</Form.Label><Form.Control value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Form.Group>
      <Form.Group className="kkd-field"><Form.Label>Annual Target Value</Form.Label><Form.Control type="number" value={form.annual_target_value} onChange={(e) => setForm({ ...form, annual_target_value: e.target.value })} /></Form.Group>
      <div className="row g-3">
        <div className="col-sm-6">
          <Form.Group className="kkd-field">
            <Form.Label>Gate: min primary rating</Form.Label>
            <Form.Select value={form.gate_min_primary_rating} onChange={(e) => setForm({ ...form, gate_min_primary_rating: Number(e.target.value) })}>{[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r}</option>)}</Form.Select>
          </Form.Group>
        </div>
        <div className="col-sm-6">
          <Form.Group className="kkd-field">
            <Form.Label>Cap rating when missed</Form.Label>
            <Form.Select value={form.cap_rating_when_missed} onChange={(e) => setForm({ ...form, cap_rating_when_missed: Number(e.target.value) })}>{[4, 3, 2, 1].map((r) => <option key={r} value={r}>{r}</option>)}</Form.Select>
          </Form.Group>
        </div>
      </div>
      <div className="kkd-form-footer kkd-form-footer-inline">
        {onCancel ? <Button className="unit-btn unit-btn-light" onClick={onCancel}>Cancel</Button> : null}
        <Button className="unit-btn unit-btn-primary" onClick={handleSave} disabled={saving}>{saving ? <Spinner size="sm" animation="border" /> : "Save"}</Button>
      </div>
    </div>
  );
}

// =============================================================================
// Tab: Evidence Mapping
// =============================================================================
function EvidenceMappingTab({ config, onChanged }) {
  const allKpis = useMemo(() => config.kras.flatMap((kra) => kra.kpis.map((kpi) => ({ ...kpi, kra_name: kra.name }))), [config]);
  const [selectedKpiId, setSelectedKpiId] = useState(allKpis[0]?.id || null);
  const selected = allKpis.find((k) => k.id === selectedKpiId);

  return (
    <div className="kkd-evidence-tab">
      <div className="unit-card kkd-evidence-card">
        <div className="kkd-card-body">
          <Form.Group className="kkd-field kkd-kpi-select-field">
            <Form.Label>KPI</Form.Label>
            <Form.Select
              value={selectedKpiId || ""}
              onChange={(e) => setSelectedKpiId(Number(e.target.value))}
              title={selected ? `${selected.kra_name} — ${selected.name} (${selected.evidence_source})` : undefined}
            >
              {allKpis.map((k) => <option key={k.id} value={k.id}>{k.kra_name} — {k.name} ({k.evidence_source})</option>)}
            </Form.Select>
          </Form.Group>
          {!selected ? <div className="text-muted kkd-empty-note">No KPIs configured yet.</div>
            : selected.evidence_source === "TASK_TYPE" ? <TaskTypeMappingEditor key={selected.id} kpi={selected} onChanged={onChanged} />
            : selected.evidence_source === "FORM" ? <FormMappingEditor key={selected.id} kpi={selected} onChanged={onChanged} />
            : <div className="alert alert-secondary kkd-alert-inline">Evidence source is <code>{selected.evidence_source}</code> — no automatic mapping applies. {selected.evidence_source === "MANUAL" ? "This KPI's business metric must be entered directly; the dashboard will show \"Insufficient Data\" until a value exists." : null}</div>}
        </div>
      </div>
    </div>
  );
}

function TaskTypeMappingEditor({ kpi, onChanged }) {
  const [allTaskTypes, setAllTaskTypes] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => { api.get("/admin/kra-kpi/task-types").then((res) => setAllTaskTypes(res.data?.data || [])); }, []);

  const mapped = kpi.task_type_mappings.filter((m) => m.is_active);
  const mappedIds = new Set(mapped.map((m) => Number(m.task_type_id)));
  const available = allTaskTypes.filter((t) => !mappedIds.has(t.id));

  const handleAdd = async (taskTypeId) => {
    try {
      await api.post(`/admin/kra-kpi/kpis/${kpi.id}/task-type-mappings`, { task_type_ids: [taskTypeId] });
      toast.success("Task type mapped.");
      setAdding(false);
      onChanged();
    } catch (err) { toast.error(errMsg(err, "Failed to add mapping.")); }
  };

  const handleRemove = async (mappingId) => {
    try { await api.delete(`/admin/kra-kpi/task-type-mappings/${mappingId}`); onChanged(); } catch (err) { toast.error(errMsg(err, "Failed to remove mapping.")); }
  };

  return (
    <div className="kkd-mapping-section">
      <h3 className="kkd-section-heading">Task Type Mapping</h3>
      <p className="kkd-helper-text">Tasks whose Task Type has an active mapping here automatically become evidence — never matched by title keywords.</p>
      <div className="kkd-chip-row">
        {mapped.map((m) => (
          <span key={m.id} className="kkd-chip">
            {m.task_type_name}
            <Can moduleKey={MODULE} actionKey="manage_evidence_mapping">
              <button
                type="button"
                className="kkd-chip-remove"
                onClick={() => handleRemove(m.id)}
                aria-label={`Remove ${m.task_type_name} mapping`}
                title={`Remove ${m.task_type_name}`}
              >
                <FaTimes aria-hidden="true" />
              </button>
            </Can>
          </span>
        ))}
        {!mapped.length ? <span className="text-muted kkd-empty-note">No task types mapped yet.</span> : null}
      </div>
      <Can moduleKey={MODULE} actionKey="manage_evidence_mapping">
        {adding ? (
          <Form.Select className="kkd-add-chip-select" onChange={(e) => e.target.value && handleAdd(Number(e.target.value))} defaultValue="">
            <option value="" disabled>Select a task type...</option>
            {available.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Form.Select>
        ) : (
          <Button className="kkd-add-chip-btn" onClick={() => setAdding(true)}>
            <FaPlus aria-hidden="true" /> Add Task Type
          </Button>
        )}
      </Can>
    </div>
  );
}

function FormMappingEditor({ kpi, onChanged }) {
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [ratingFields, setRatingFields] = useState([]);
  const [allFields, setAllFields] = useState([]);
  const [selectedFieldIds, setSelectedFieldIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/kra-kpi/forms").then((res) => setForms(res.data?.data || [])); }, []);

  const mapped = kpi.form_mappings.filter((m) => m.is_active);
  const mappedFormId = mapped[0]?.form_id || null;

  useEffect(() => {
    if (!selectedFormId) return;
    api.get(`/admin/kra-kpi/forms/${selectedFormId}/fields`).then((res) => setRatingFields(res.data?.data?.fields || []));
    api.get(`/admin/kra-kpi/forms/${selectedFormId}/all-fields`).then((res) => setAllFields(res.data?.data?.fields || []));
  }, [selectedFormId]);

  useEffect(() => {
    if (mappedFormId) {
      setSelectedFormId(mappedFormId);
      setSelectedFieldIds(new Set(mapped.filter((m) => m.form_field_id).map((m) => Number(m.form_field_id))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpi.id]);

  const toggleField = (fieldId) => {
    setSelectedFieldIds((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId); else next.add(fieldId);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/kra-kpi/kpis/${kpi.id}/form-mappings`, { form_id: selectedFormId, form_field_ids: [...selectedFieldIds] });
      toast.success("Form mapping saved.");
      onChanged();
    } catch (err) { toast.error(errMsg(err, "Failed to save mapping.")); } finally { setSaving(false); }
  };

  const subjectField = allFields.find((f) => f.is_subject_field);

  const handleSetSubject = async (fieldId, isSubject) => {
    try {
      await api.patch(`/admin/kra-kpi/form-fields/${fieldId}/subject`, { is_subject_field: isSubject });
      const res = await api.get(`/admin/kra-kpi/forms/${selectedFormId}/all-fields`);
      setAllFields(res.data?.data?.fields || []);
      toast.success(isSubject ? "Subject field set." : "Subject field unset.");
    } catch (err) { toast.error(errMsg(err, "Failed to update subject field.")); }
  };

  return (
    <div className="kkd-mapping-section">
      <h3 className="kkd-section-heading">Form Evidence Mapping</h3>
      <p className="kkd-helper-text">A KPI may map to multiple rating fields on the same form (e.g. Team Satisfaction Survey's 5 fields all count toward one KPI).</p>

      <Form.Group className="kkd-field kkd-kpi-select-field">
        <Form.Label>Form</Form.Label>
        <Form.Select value={selectedFormId || ""} onChange={(e) => setSelectedFormId(Number(e.target.value) || null)}>
          <option value="">Select a form...</option>
          {forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </Form.Select>
      </Form.Group>

      {selectedFormId ? (
        <>
          <div className="kkd-field">
            <Form.Label>Subject / Target Employee Field <span className="kkd-helper-text-inline">(which field identifies the employee being evaluated?)</span></Form.Label>
            {allFields.map((f) => (
              <Form.Check
                key={f.id} type="radio" name="subject-field" label={f.label}
                checked={f.is_subject_field === 1}
                onChange={() => handleSetSubject(f.id, true)}
              />
            ))}
            {subjectField ? <div className="kkd-inline-success">Subject field: {subjectField.label}</div> : <div className="kkd-inline-warning">No subject field configured — evidence will fall back to project/task ownership.</div>}
          </div>

          <Form.Group className="kkd-field">
            <Form.Label>Rating Fields (select all that contribute to this KPI)</Form.Label>
            {ratingFields.map((f) => (
              <Form.Check key={f.id} type="checkbox" label={f.label} checked={selectedFieldIds.has(f.id)} onChange={() => toggleField(f.id)} />
            ))}
            {!ratingFields.length ? <div className="text-muted small">No rating fields available on the published version of this form.</div> : null}
          </Form.Group>

          <Can moduleKey={MODULE} actionKey="manage_evidence_mapping">
            <Button className="unit-btn unit-btn-primary" onClick={handleSave} disabled={saving || !selectedFieldIds.size}>{saving ? <Spinner size="sm" animation="border" /> : "Save Mapping"}</Button>
          </Can>
        </>
      ) : null}
    </div>
  );
}

// =============================================================================
// Tab: Employee Assignments
// =============================================================================
function AssignmentsTab({ configurationId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedUser, setPickedUser] = useState(null);
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/kra-kpi/configurations/${configurationId}/assignments`);
      setRows(res.data?.data || []);
    } catch (err) { toast.error(errMsg(err, "Failed to load assignments.")); } finally { setLoading(false); }
  }, [configurationId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleAssign = async () => {
    if (!pickedUser) return;
    setSaving(true);
    try {
      await api.post("/admin/kra-kpi/assignments", { configuration_id: configurationId, user_id: pickedUser, effective_from: effectiveFrom });
      toast.success("Employee assigned.");
      setPickedUser(null);
      fetchRows();
    } catch (err) { toast.error(errMsg(err, "Failed to assign.")); } finally { setSaving(false); }
  };

  const handleToggle = async (row) => {
    try {
      await api.patch(`/admin/kra-kpi/assignments/${row.id}/status`, { is_active: !row.is_active });
      fetchRows();
    } catch (err) { toast.error(errMsg(err, "Failed to update.")); }
  };

  return (
    <div className="kkd-assignments-tab">
      <Can moduleKey={MODULE} actionKey="assign_employees">
        <div className="unit-card kkd-assign-form-card">
          <div className="kkd-card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <Form.Group className="kkd-field">
                  <Form.Label>Employee</Form.Label>
                  <EmployeePicker endpoint="/admin/kra-kpi/employees/search" value={pickedUser} onChange={(id) => setPickedUser(id)} />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="kkd-field">
                  <Form.Label>Effective From</Form.Label>
                  <Form.Control type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Button className="unit-btn unit-btn-primary w-100" onClick={handleAssign} disabled={saving || !pickedUser}>
                  {saving ? <Spinner size="sm" animation="border" /> : "Assign"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Can>

      <div className="unit-card kkd-assign-table-card">
        <div className="unit-table-wrapper">
          <Table responsive className="unit-table kkd-assign-table">
            <colgroup>
              <col style={{ width: "38%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "14%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Effective From</th>
                <th>Effective To</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-3"><Spinner size="sm" animation="border" /></td></tr>
              ) : rows.length ? rows.map((row) => (
                <tr key={row.id}>
                  <td className="kkd-employee-cell">
                    <div className="kkd-employee-name">{row.user_name}</div>
                    <small className="text-muted kkd-employee-email">{row.user_email}</small>
                  </td>
                  <td className="kkd-date-cell">{formatDate(row.effective_from)}</td>
                  <td className="kkd-date-cell">{row.effective_to ? formatDate(row.effective_to) : "—"}</td>
                  <td>
                    <Badge className={`unit-status${row.is_active ? "" : " unit-status-inactive"}`}>
                      {row.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Can moduleKey={MODULE} actionKey="assign_employees">
                      <Button
                        className={`kkd-toggle-text-btn${row.is_active ? " kkd-toggle-text-btn--warn" : " kkd-toggle-text-btn--ok"}`}
                        title={row.is_active ? "Deactivate assignment" : "Activate assignment"}
                        aria-label={`${row.is_active ? "Deactivate" : "Activate"} assignment for ${row.user_name || "employee"}`}
                        onClick={() => handleToggle(row)}
                      >
                        {row.is_active ? "Deactivate" : "Activate"}
                      </Button>
                    </Can>
                  </td>
                </tr>
              )) : <tr><td colSpan={5} className="text-center text-muted py-3">No employees assigned yet.</td></tr>}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}
